import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { GoogleLogin } from '@react-oauth/google';
import {
  Sparkles,
  X,
  Send,
  User,
  Loader2,
  MessageSquare,
  BookOpen,
  TrendingUp,
  LogIn,
  Minimize2,
  Lock,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { mentorChat, mentorChatPublic, googleLogin } from '../api/client';
import type { User as UserType } from '../types/auth';
import Button from './ui/Button';

interface Message {
  id?: number;
  role: 'user' | 'assistant';
  content: string;
  referenced_courses?: Array<{
    id: number;
    title: string;
    slug: string;
    price: number;
  }>;
  created_at?: string;
}

const SUGGESTIONS = [
  'What AI skills for ₹15L PA?',
  'Career gap analysis',
  'Which course first?',
  'Interview prep tips',
];

const ANON_STORAGE_KEY = 'mentor_anonymous_chat';
const ANON_MESSAGE_LIMIT = 5;

// ─── Simple Markdown Renderer ───
function MarkdownText({ text }: { text: string }): React.JSX.Element {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;

  const renderInline = (line: string) => {
    const parts: React.ReactNode[] = [];
    const regex = /\*\*(.*?)\*\*/g;
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(line)) !== null) {
      if (match.index > lastIndex) {
        parts.push(line.slice(lastIndex, match.index));
      }
      parts.push(<strong key={match.index} className="font-bold text-slate-900">{match[1]}</strong>);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < line.length) {
      parts.push(line.slice(lastIndex));
    }
    return parts.length > 0 ? parts : line;
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === '') {
      i++;
      continue;
    }

    // Heading: ## text
    if (trimmed.startsWith('## ')) {
      elements.push(
        <h4 key={i} className="text-xs font-bold text-slate-900 mt-2 mb-1">
          {renderInline(trimmed.slice(3))}
        </h4>
      );
      i++;
      continue;
    }

    // Numbered list: 1. item
    if (/^\d+\.\s/.test(trimmed)) {
      const items: React.ReactNode[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
        const itemText = lines[i].trim().replace(/^\d+\.\s*/, '');
        items.push(
          <li key={i} className="ml-4 text-xs text-slate-700 leading-relaxed">
            <span className="text-amber-500 font-bold mr-1">{items.length + 1}.</span>
            {renderInline(itemText)}
          </li>
        );
        i++;
      }
      elements.push(<ol key={`ol-${i}`} className="list-none space-y-0.5 my-1">{items}</ol>);
      continue;
    }

    // Bullet list: - item or * item or • item
    if (/^[-*•]\s/.test(trimmed)) {
      const items: React.ReactNode[] = [];
      while (i < lines.length && /^[-*•]\s/.test(lines[i].trim())) {
        const itemText = lines[i].trim().replace(/^[-*•]\s*/, '');
        items.push(
          <li key={i} className="flex items-start gap-1.5 text-xs text-slate-700 leading-relaxed">
            <span className="w-1 h-1 rounded-full bg-amber-400 mt-1.5 shrink-0" />
            {renderInline(itemText)}
          </li>
        );
        i++;
      }
      elements.push(<ul key={`ul-${i}`} className="list-none space-y-0.5 my-1">{items}</ul>);
      continue;
    }

    // Regular paragraph
    elements.push(
      <p key={i} className="text-xs text-slate-700 leading-relaxed">
        {renderInline(trimmed)}
      </p>
    );
    i++;
  }

  return <div className="space-y-1">{elements}</div>;
}

export default function MentorWidget(): React.JSX.Element | null {
  const { isAuthenticated, user, setAuth } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Load anonymous messages from localStorage
  useEffect(() => {
    if (!isAuthenticated) {
      try {
        const stored = localStorage.getItem(ANON_STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed.messages)) {
            setMessages(parsed.messages);
            setHasInteracted(parsed.hasInteracted ?? parsed.messages.length > 0);
          }
        }
      } catch {
        // ignore parse errors
      }
    }
  }, [isAuthenticated]);

  // Save anonymous messages to localStorage
  useEffect(() => {
    if (!isAuthenticated && messages.length > 0) {
      localStorage.setItem(ANON_STORAGE_KEY, JSON.stringify({ messages, hasInteracted }));
    }
  }, [messages, hasInteracted, isAuthenticated]);

  // Clear anonymous storage on login
  useEffect(() => {
    if (isAuthenticated) {
      localStorage.removeItem(ANON_STORAGE_KEY);
    }
  }, [isAuthenticated]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Show tooltip after 5 seconds on first visit
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => setShowTooltip(true), 5000);
      const hideTimer = setTimeout(() => setShowTooltip(false), 12000);
      return () => {
        clearTimeout(timer);
        clearTimeout(hideTimer);
      };
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  // Click outside to close (desktop only)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (window.innerWidth >= 640 && panelRef.current && !panelRef.current.contains(e.target as Node)) {
        const fab = document.getElementById('mentor-fab');
        if (fab && !fab.contains(e.target as Node)) {
          setIsOpen(false);
        }
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      setIsLoading(true);
      const idToken = credentialResponse.credential;
      if (!idToken) {
        toast.show('Google login failed: no token received', 'error');
        return;
      }
      const { getReferralCode } = await import('../utils/utm');
      const referralCode = getReferralCode();
      const response = await googleLogin(idToken, '', 'web', undefined, referralCode || undefined);
      if (response.error) {
        toast.show(response.error || 'Login failed', 'error');
        return;
      }
      const { access_token, refresh_token, user } = response;
      if (access_token && user) {
        setAuth(access_token, refresh_token, user as UserType);
        toast.show('Welcome, ' + user.name + '!', 'success');
      } else {
        toast.show('Invalid login response from server', 'error');
      }
    } catch (err: any) {
      toast.show(err.message || 'Google sign-in failed', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    toast.show('Google sign-in failed. Please try again.', 'error');
  };

  const isAnonymousLimitReached = !isAuthenticated && messages.filter(m => m.role === 'user').length >= ANON_MESSAGE_LIMIT;

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    if (!isAuthenticated && isAnonymousLimitReached) return;

    const userMessage = input.trim();
    setInput('');
    setError('');
    setHasInteracted(true);

    const tempUserMsg: Message = { role: 'user', content: userMessage };
    setMessages((prev) => [...prev, tempUserMsg]);
    setIsLoading(true);

    try {
      if (!isAuthenticated) {
        // Anonymous chat
        const history = messages.slice(-10).map(m => ({ role: m.role, content: m.content }));
        const res = await mentorChatPublic({ message: userMessage, history });

        if (res.reply) {
          const assistantMsg: Message = {
            role: 'assistant',
            content: res.reply,
            referenced_courses: res.referenced_courses,
          };
          setMessages((prev) => [...prev, assistantMsg]);
        }
      } else {
        // Authenticated chat
        const res = await mentorChat({
          message: userMessage,
          conversation_id: conversationId ?? undefined,
        });

        if (res.reply) {
          const assistantMsg: Message = {
            role: 'assistant',
            content: res.reply,
            referenced_courses: res.referenced_courses,
          };
          setMessages((prev) => [...prev, assistantMsg]);
          if (res.conversation_id && !conversationId) {
            setConversationId(res.conversation_id);
          }
        }
      }
    } catch (err: any) {
      const msg = err?.message || 'Failed to send message';
      setError(msg);
      setMessages((prev) => prev.slice(0, -1));
      setInput(userMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (text: string) => {
    setInput(text);
    inputRef.current?.focus();
  };

  const handleLogin = () => {
    localStorage.setItem('redirect_after_login', window.location.pathname);
    navigate('/login');
    setIsOpen(false);
  };

  const handleNewChat = () => {
    setMessages([]);
    setConversationId(null);
    setError('');
    setHasInteracted(false);
    if (!isAuthenticated) {
      localStorage.removeItem(ANON_STORAGE_KEY);
    }
  };

  const anonUserCount = messages.filter(m => m.role === 'user').length;

  return (
    <>
      {/* Floating Action Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            id="mentor-fab"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            className="fixed bottom-6 right-6 z-50"
          >
            {/* Tooltip */}
            <AnimatePresence>
              {showTooltip && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.9 }}
                  className="absolute bottom-full right-0 mb-3 w-64 bg-white rounded-2xl shadow-xl border border-slate-200 p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shrink-0">
                      <Sparkles className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Hey {user?.name?.split(' ')[0] || 'there'}!</p>
                      <p className="text-xs text-slate-500 mt-0.5">Ask me about your career, courses, or interview prep.</p>
                    </div>
                  </div>
                  <div className="absolute bottom-[-6px] right-6 w-3 h-3 bg-white border-b border-r border-slate-200 rotate-45" />
                </motion.div>
              )}
            </AnimatePresence>

            <button
              onClick={() => {
                setIsOpen(true);
                setShowTooltip(false);
              }}
              className="group relative w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 hover:scale-105 transition-all duration-200 flex items-center justify-center"
            >
              <MessageSquare className="w-6 h-6 group-hover:scale-110 transition-transform" />
              <span className="absolute top-0 right-0 w-3.5 h-3.5 bg-red-500 rounded-full border-2 border-white" />
              <span className="absolute inset-0 rounded-full animate-ping bg-orange-400 opacity-20" style={{ animationDuration: '3s' }} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={panelRef}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className={`fixed z-50 bg-white shadow-2xl border border-slate-200 overflow-hidden flex flex-col
              ${isMobile
                ? 'bottom-0 left-0 right-0 h-[92vh] max-h-[800px] rounded-t-3xl border-b-0'
                : 'bottom-6 right-6 w-[400px] h-[520px] max-h-[85vh] rounded-3xl'
              }`}
          >
            {/* Mobile drag handle */}
            {isMobile && (
              <div className="shrink-0 pt-3 pb-1 flex justify-center">
                <div className="w-10 h-1 bg-slate-300 rounded-full" />
              </div>
            )}

            {/* Header */}
            <div className="h-14 bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-between px-4 shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-white">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white">Mentor</h3>
                  <p className="text-[10px] text-white/80 leading-none">AI Career Companion</p>
                </div>
              </div>
              <div className="flex items-center gap-0.5">
                {messages.length > 0 && (
                  <button
                    onClick={handleNewChat}
                    className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
                    title="New chat"
                  >
                    <Minimize2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto bg-slate-50/50 p-4 space-y-4">
              {messages.length === 0 && !hasInteracted ? (
                <div className="space-y-4">
                  {/* Welcome */}
                  <div className="text-center space-y-2 pt-3">
                    <div className="w-10 h-10 mx-auto rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shadow-md">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <h4 className="font-bold text-slate-900 text-sm">
                      Hey {user?.name?.split(' ')[0] || 'there'}!
                    </h4>
                    <p className="text-xs text-slate-500">
                      Ask me about your career, courses, or interview prep.
                    </p>
                    {!isAuthenticated && (
                      <p className="text-[10px] text-slate-400">
                        No account needed — try {ANON_MESSAGE_LIMIT} messages for free.
                      </p>
                    )}
                  </div>

                  {/* Suggestions */}
                  <div className="space-y-2">
                    {SUGGESTIONS.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => handleSuggestionClick(s)}
                        className="w-full text-left px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-600 hover:border-amber-300 hover:bg-amber-50/50 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((msg, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {msg.role === 'assistant' && (
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shrink-0 mt-0.5">
                          <Sparkles className="w-3 h-3" />
                        </div>
                      )}

                      <div className={`max-w-[85%] space-y-1.5 ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                        <div
                          className={`rounded-2xl leading-relaxed ${
                            msg.role === 'user'
                              ? 'px-3 py-2 bg-amber-500 text-white rounded-br-md text-xs'
                              : 'px-3.5 py-2.5 bg-white border border-slate-200 text-slate-800 rounded-bl-md shadow-sm text-[13px]'
                          }`}
                        >
                          {msg.role === 'assistant' ? (
                            <MarkdownText text={msg.content} />
                          ) : (
                            <div className="whitespace-pre-wrap">{msg.content}</div>
                          )}
                        </div>

                        {/* Course Recommendations */}
                        {msg.role === 'assistant' && msg.referenced_courses && msg.referenced_courses.length > 0 && (
                          <div className="space-y-1.5">
                            {msg.referenced_courses.map((course) => (
                              <button
                                key={course.id}
                                onClick={() => {
                                  setIsOpen(false);
                                  navigate(`/courses/${course.slug}`);
                                }}
                                className="flex items-center gap-2 w-full text-left bg-white border border-slate-200 hover:border-amber-300 hover:shadow-sm rounded-xl p-2 transition-all"
                              >
                                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600 shrink-0">
                                  <BookOpen className="w-4 h-4" />
                                </div>
                                <div className="min-w-0">
                                  <div className="text-xs font-semibold text-slate-800 truncate">{course.title}</div>
                                  <div className="text-[10px] text-amber-600 font-medium">₹{course.price}</div>
                                </div>
                                <TrendingUp className="w-3 h-3 text-slate-300 shrink-0 ml-auto" />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {msg.role === 'user' && (
                        <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 shrink-0 mt-0.5">
                          <User className="w-3 h-3" />
                        </div>
                      )}
                    </motion.div>
                  ))}

                  {isLoading && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-2">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shrink-0">
                        <Sparkles className="w-3 h-3" />
                      </div>
                      <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-3 py-2 shadow-sm">
                        <div className="flex gap-1">
                          <span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </>
              )}

              {/* Anonymous limit reached banner */}
              {isAnonymousLimitReached && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center space-y-2"
                >
                  <div className="flex items-center justify-center gap-1.5 text-amber-700 text-xs font-semibold">
                    <Lock className="w-3.5 h-3.5" />
                    Free message limit reached
                  </div>
                  <p className="text-[11px] text-amber-600">
                    Sign in to continue chatting and save your conversations.
                  </p>
                  <div className="flex justify-center">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      size="medium"
                      width="260"
                      text="continue_with"
                      theme="outline"
                      shape="pill"
                    />
                  </div>
                  <button
                    onClick={handleLogin}
                    className="text-[11px] text-slate-400 hover:text-amber-600 transition-colors flex items-center gap-1 mx-auto"
                  >
                    <LogIn className="w-3 h-3" />
                    Or use email / password
                  </button>
                </motion.div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700">
                  {error}
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="border-t border-slate-200 bg-white p-3 shrink-0">
              {!isAuthenticated && anonUserCount > 0 && !isAnonymousLimitReached && (
                <div className="flex items-center justify-between mb-1.5 px-0.5">
                  <span className="text-[10px] text-slate-400">
                    {ANON_MESSAGE_LIMIT - anonUserCount} free message{ANON_MESSAGE_LIMIT - anonUserCount !== 1 ? 's' : ''} remaining
                  </span>
                </div>
              )}
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={isAnonymousLimitReached ? 'Sign in to continue...' : 'Ask Mentor...'}
                    rows={1}
                    disabled={isAnonymousLimitReached}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 pr-10 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 resize-none max-h-24 min-h-[36px] disabled:opacity-50"
                    style={{ height: 'auto' }}
                    onInput={(e) => {
                      const target = e.target as HTMLTextAreaElement;
                      target.style.height = 'auto';
                      target.style.height = target.scrollHeight + 'px';
                    }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={!input.trim() || isLoading || isAnonymousLimitReached}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-40 transition-colors"
                  >
                    <Send className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
